/**
 * Item with name for filtering
 */
interface NamedItem {
  name: string;
}

/**
 * Item with isEssential flag
 */
interface EssentialItem {
  isEssential?: boolean;
}

/**
 * Create a Set of lowercase item names for duplicate checking
 */
export function createNameSet(items: NamedItem[]): Set<string> {
  return new Set(items.map((item) => item.name.toLowerCase()));
}

/**
 * Filter items by excluding those already present (case-insensitive)
 */
export function filterByExistingNames<T extends NamedItem>(
  newItems: T[],
  existingItems: NamedItem[]
): T[] {
  const existingNames = createNameSet(existingItems);
  return newItems.filter((item) => !existingNames.has(item.name.toLowerCase()));
}

/**
 * Get indices of essential items in an array
 */
export function getEssentialIndices(items: EssentialItem[]): Set<number> {
  return new Set(items.map((item, i) => (item.isEssential ? i : -1)).filter((i) => i !== -1));
}

/**
 * Toggle a value in a Set (add if not present, remove if present)
 */
export function toggleSetItem<T>(set: Set<T>, item: T): Set<T> {
  const next = new Set(set);
  if (next.has(item)) {
    next.delete(item);
  } else {
    next.add(item);
  }
  return next;
}

/**
 * Create a Set with all indices from an array
 */
export function selectAllIndices<T>(items: T[]): Set<number> {
  return new Set(items.map((_, i) => i));
}

/**
 * Extract selected items from an array based on indices
 */
export function getSelectedItems<T>(items: T[], selectedIndices: Set<number>): T[] {
  return items.filter((_, i) => selectedIndices.has(i));
}
